// components/common/MultiSelectAutocomplete.tsx

import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  ListItemText,
  TextField,
  Tooltip,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import "../../../../common/components/autocomplete-component/autocomplete.scss"
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { MultiSelectAutocompleteProps, OptionType } from "../lead-assignment-props";

const CustomPopper = styled(Popper)({
  zIndex: '5 !important',
});

const MultiSelectAutocomplete: React.FC<MultiSelectAutocompleteProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select",
  noResultsTxt,
}) => {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value)
  );

  const handleChange = (_event: any, selected: OptionType[]) => {
    const selectedIds = selected.map((opt) => opt.value);
    onChange(selectedIds);
  };

  const [inputValue, setInputValue] = useState("");
  const filterOptions = React.useCallback((opts: OptionType[]) => {
    const search = inputValue.trim().toLowerCase();
    if (!search && selectedValues?.length === 0) {
      return opts.slice(0, 100);
    }
    return opts
      .filter((opt) => opt.label.toLowerCase().includes(search))
      .slice(0, 100);
  }, [inputValue, selectedValues]);

  return (
    <div className="autocomplete-multi-select-input-component">
      <div>
        <Autocomplete
          disablePortal
          multiple
          inputValue={inputValue}
          options={options}
          disableCloseOnSelect
          popupIcon={<KeyboardArrowDownIcon />}
          value={selectedOptions}
          filterOptions={filterOptions}
          onChange={handleChange}
          getOptionLabel={(option) => option.label}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "clear") {
              setInputValue(newInputValue);
            } else if (reason === "input") {
              setInputValue(newInputValue);
            }
          }}

          PopperComponent={CustomPopper}
          renderTags={() => null}
          componentsProps={{
            paper: {
              sx: {
                padding: '0px !important',
                maxHeight: "170px"
              }
            }
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} style={{ padding: 0 }}>
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "35px",
                  px: 2,
                  py: 1,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#8879501a",
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  }
                }}
              >
                <ListItemText primary={option.label} />
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={selected}

                  style={{ marginLeft: 8 }}
                />
              </Box>
            </li>
          )}
          renderInput={(params) => {
            const selectedLabels = selectedOptions.map((opt) => opt.label).join(", ");
            return (
              <TextField
                {...params}
                placeholder={selectedOptions.length === 0 ? placeholder : ""}
                sx={{
                  "& input::placeholder": {
                    color: "#676767",
                    opacity: 1,
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  style: {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  startAdornment: (
                    <Tooltip title={selectedLabels} placement="top">
                      <div
                        style={{
                          maxWidth: "calc(100% - 30px)", // Leave space for icon
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: "#000",
                          fontSize: "14px",
                        }}
                      >
                        {selectedLabels.length > 30
                          ? selectedLabels.slice(0, 30) + "..."
                          : selectedLabels}
                      </div>
                    </Tooltip>
                  )
                }}
                inputProps={{
                  ...params.inputProps,
                  style: {
                    paddingLeft: selectedOptions.length > 0 ? 4 : undefined,
                    color: selectedOptions?.length > 0 ? "white" : "",
                  },

                }}
              />
            );
          }}
          noOptionsText={noResultsTxt || "No options found"}
          // clearOnEscape
        />
      </div>
    </div>
  );
};

export default MultiSelectAutocomplete;
